// The repo check (card #2542): can cortex render this branch of the project's repo, module by module.
//   RepoStatusCard  Settings → Repository status: every module (renderable / renders with gaps / broken,
//                   with file:line), what this environment must configure, the JSON Schemas, the files
//                   for developers' AI tools (AGENTS.md, CLAUDE.md; ADR 0030), and for a
//                   repo not in the cortex format: Adopt (a Board card: an agent converts it in one commit
//                   on a branch, previewed first) or link it read-only.
//   RepoCheckStrip  one line on a project page when its module is not renderable (Layout mounts it).
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  FileJson,
  GitBranch,
  Loader2,
  Lock,
  RefreshCw,
  Settings2,
  Wand2,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import { api } from '../../platform/index.ts';
import { projectPath } from '../../platform/index.ts';
import { boardCards } from '../../platform/index.ts';
import { BTN, PRIMARY } from './common.tsx';

type Status = 'renderable' | 'gaps' | 'broken';
interface Problem {
  check: string;
  file: string | null;
  line: number | null;
  message: string;
}
interface ConfigureItem {
  kind: string;
  module: string;
  key: string;
  label: string;
  detail: string;
  file: string | null;
  line: number | null;
  ref?: string;
  configured?: boolean | null;
}
interface ModuleResult {
  id: string;
  label: string;
  page: string | null;
  status: Status;
  files: number;
  broken: Problem[];
  gaps: Problem[];
  configure: ConfigureItem[];
  notes: string[];
}
/** Nothing to check (the design is not on git, the project has no repository, the branch no commit). */
export interface NoRepoCheck {
  status: 'no_repo';
  reason: 'not_on_git' | 'no_workspace' | 'no_branch';
  message: string;
  branch: string;
}
export interface RepoCheck {
  status: Status | 'not_cortex';
  commit: string | null;
  head: string | null;
  current: boolean;
  checkedAt: string;
  format: {
    found: boolean;
    format: string | null;
    version: number | null;
    supported: number;
    ok: boolean;
    message: string | null;
  };
  modules: ModuleResult[];
  configure: ConfigureItem[];
  schemas: { published: boolean; missing: string[]; stale: string[] };
  /** The files for developers' AI tools (AGENTS.md, CLAUDE.md; ADR 0030): null when not checked. */
  aiTools?: { missing: string[]; stale: string[] } | null;
  notCortex: { reason: string; hints: string[] } | null;
  /** Files the index skipped at this commit (each is also a broken problem of its module). */
  quarantined?: { path: string; reason: string }[];
  stats: { files: number; designFiles: number; ms: number };
  link: { mode: 'read_only'; setBy: string; setAt: string } | null;
  adopt: { title: string; details: string; branch: string } | null;
}

const LABEL: Record<RepoCheck['status'], string> = {
  renderable: 'Renderable',
  gaps: 'Renders with gaps',
  broken: 'Broken',
  not_cortex: 'Not a cortex repo',
};
const TONE: Record<RepoCheck['status'], string> = {
  renderable:
    'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900',
  gaps: 'text-amber-800 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900',
  broken: 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/40 dark:border-red-900',
  not_cortex: 'text-page-text bg-page-bg border-page-divider',
};
const Icon = ({ s, size = 14 }: { s: RepoCheck['status']; size?: number }) =>
  s === 'renderable' ? (
    <CheckCircle2 size={size} />
  ) : s === 'broken' ? (
    <XCircle size={size} />
  ) : (
    <AlertTriangle size={size} />
  );

export function StatusPill({ status, testid }: { status: RepoCheck['status']; testid?: string }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${TONE[status]}`}
    >
      <Icon s={status} size={12} />
      {LABEL[status]}
    </span>
  );
}

const loc = (p: { file: string | null; line: number | null }) =>
  p.file ? `${p.file}${p.line ? `:${p.line}` : ''}` : 'repo';

/** The check of a branch head; re-read when the branch moves (every save goes through a commit). */
export function useRepoCheck(slug: string | null, branch: string | null) {
  const [check, setCheck] = React.useState<RepoCheck | NoRepoCheck | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const load = React.useCallback(
    async (rerun = false) => {
      if (!slug || !branch) return;
      const opts = { params: { path: { slug, branch } } };
      const { data, error: e } = rerun
        ? await api.POST('/api/git/projects/{slug}/branches/{branch}/check', opts)
        : await api.GET('/api/git/projects/{slug}/branches/{branch}/check', opts);
      if (e) setError((e as { message?: string }).message ?? 'The repo check could not be read');
      else {
        setError(null);
        setCheck(data as RepoCheck | NoRepoCheck);
      }
    },
    [slug, branch],
  );
  React.useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15_000);
    return () => clearInterval(t);
  }, [load]);
  return { check, error, reload: load };
}

function ProblemList({ items, tone }: { items: Problem[]; tone: 'broken' | 'gap' }) {
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-1">
      {items.map((p, i) => (
        <li key={`${p.file}:${p.line}:${i}`} className="text-xs flex gap-2">
          <code
            className={`shrink-0 ${tone === 'broken' ? 'text-red-700 dark:text-red-300' : 'text-page-muted'}`}
          >
            {loc(p)}
          </code>
          <span className="text-page-text break-words min-w-0">{p.message}</span>
        </li>
      ))}
    </ul>
  );
}

function ModuleRow({ m, slug, branch }: { m: ModuleResult; slug: string; branch: string }) {
  const [open, setOpen] = React.useState(m.status === 'broken');
  const count = [
    m.broken.length ? `${m.broken.length} broken` : '',
    m.gaps.length ? `${m.gaps.length} gap${m.gaps.length === 1 ? '' : 's'}` : '',
    m.configure.length ? `${m.configure.length} to configure` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li
      className="border-t border-page-divider first:border-t-0 py-2"
      data-testid={`repo-check-module-${m.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          className="bg-transparent border-0 p-0 cursor-pointer text-sm font-medium text-page-text text-left"
          onClick={() => setOpen((v) => !v)}
        >
          {m.label}
        </button>
        <StatusPill status={m.status} />
        <span className="text-xs text-page-muted">
          {count || `${m.files} file${m.files === 1 ? '' : 's'}`}
        </span>
        {m.page && (
          <Link className="ml-auto text-xs text-nav-active" to={projectPath(m.page, slug, branch)}>
            Open page
          </Link>
        )}
      </div>
      {open && (
        <div className="mt-2 pl-2 flex flex-col gap-2">
          {m.broken.length > 0 && <ProblemList items={m.broken} tone="broken" />}
          {m.gaps.length > 0 && <ProblemList items={m.gaps} tone="gap" />}
          {m.notes.map((n) => (
            <p key={n} className="m-0 text-xs text-page-muted">
              {n}
            </p>
          ))}
        </div>
      )}
    </li>
  );
}

function ConfigureList({ items }: { items: ConfigureItem[] }) {
  if (!items.length) return null;
  return (
    <div className="mt-3" data-testid="repo-check-configure">
      <h3 className="text-sm font-medium m-0 mb-1 flex items-center gap-1.5 text-page-text">
        <Settings2 size={14} /> Configure in this environment
      </h3>
      <p className="m-0 mb-2 text-xs text-page-muted">
        Not errors: cortex fills these per environment (git holds references only).
      </p>
      <ul className="m-0 p-0 list-none flex flex-col gap-1">
        {items.map((c) => (
          <li key={`${c.module}:${c.key}`} className="text-xs flex gap-2 items-baseline">
            <span
              className={`shrink-0 inline-block w-2 h-2 rounded-full ${c.configured === true ? 'bg-emerald-500' : c.configured === false ? 'bg-amber-500' : 'bg-page-divider'}`}
              title={
                c.configured === true
                  ? 'configured here'
                  : c.configured === false
                    ? 'not configured here'
                    : 'set per environment'
              }
            />
            <span className="font-medium text-page-text">{c.label}</span>
            <span className="text-page-muted min-w-0">{c.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NotCortex({
  slug,
  branch,
  check,
  onLinked,
}: {
  slug: string;
  branch: string;
  check: RepoCheck;
  onLinked: () => void;
}) {
  const [busy, setBusy] = React.useState<string | null>(null);
  const [msg, setMsg] = React.useState<string | null>(null);
  const adopt = async () => {
    if (!check.adopt) return;
    setBusy('adopt');
    try {
      const card = await boardCards.create(slug, {
        kind: 'change_project',
        title: check.adopt.title,
        details: check.adopt.details,
        status: 'queued',
        branch,
      });
      setMsg(
        `Card #${(card as { id: number }).id} is on the Board: an agent converts the repo in one commit on its branch; preview it, then promote.`,
      );
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const link = async () => {
    setBusy('link');
    const { error } = await api.PUT('/api/git/projects/{slug}/link', {
      params: { path: { slug } },
      body: { mode: 'read_only' },
    });
    setBusy(null);
    if (error) setMsg((error as { message?: string }).message ?? 'Could not link');
    else onLinked();
  };
  return (
    <div className="mt-3 rounded-[5px] border border-page-divider p-3" data-testid="repo-check-not-cortex">
      <p className="m-0 text-sm text-page-text">{check.notCortex?.reason}</p>
      {check.notCortex?.hints.length ? (
        <p className="m-0 mt-1 text-xs text-page-muted">It holds: {check.notCortex.hints.join(', ')}.</p>
      ) : null}
      <div className="mt-3 flex gap-2 flex-wrap">
        <button
          type="button"
          className={PRIMARY}
          disabled={!!busy}
          onClick={() => void adopt()}
          data-testid="repo-check-adopt"
        >
          {busy === 'adopt' ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Adopt
        </button>
        {!check.link && (
          <button
            type="button"
            className={BTN}
            disabled={!!busy}
            onClick={() => void link()}
            data-testid="repo-check-link"
          >
            <Lock size={14} /> Link read-only
          </button>
        )}
      </div>
      <p className="m-0 mt-2 text-xs text-page-muted">
        Adopt: an agent converts the repo into the cortex format in one commit on a branch; you preview it
        before it is promoted. Read-only: cortex shows the repo and never writes to it.
      </p>
      {msg && <p className="m-0 mt-2 text-xs text-page-text">{msg}</p>}
    </div>
  );
}

/** Settings → Repository status. */
export function RepoStatusCard({ slug, branch }: { slug: string; branch: string }) {
  const { check, error, reload } = useRepoCheck(slug, branch);
  const [busy, setBusy] = React.useState<string | null>(null);
  const run = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };
  const publish = () =>
    run('schemas', async () => {
      await api.POST('/api/git/projects/{slug}/branches/{branch}/schemas', {
        params: { path: { slug, branch } },
      });
      await reload(true);
    });
  const writeAiTools = () =>
    run('ai-tools', async () => {
      await api.POST('/api/git/projects/{slug}/branches/{branch}/ai-tools', {
        params: { path: { slug, branch } },
      });
      await reload(true);
    });
  const unlink = () =>
    run('unlink', async () => {
      await api.PUT('/api/git/projects/{slug}/link', { params: { path: { slug } }, body: { mode: null } });
      await reload();
    });
  return (
    <div className="bg-card-bg rounded-[5px] border border-page-divider p-4" data-testid="repo-status-card">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <FileJson size={18} color="#377AD6" />
        <h2 className="text-base font-medium m-0 text-page-text">Repository status</h2>
        {check && check.status !== 'no_repo' && (
          <StatusPill status={check.status} testid="repo-check-status" />
        )}
        <button
          type="button"
          className={`${BTN} ml-auto`}
          disabled={!!busy}
          onClick={() => void run('rerun', () => reload(true))}
        >
          {busy === 'rerun' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Check
          again
        </button>
      </div>
      <p className="text-sm text-page-muted m-0">
        Can cortex render branch <span className="font-mono">{branch}</span> of the repo, module by module.
        Checked on every commit (a save here, a fetch, an agent), and by <code>cortex check</code> in the
        repo's own CI.
      </p>
      {error && <p className="m-0 mt-2 text-sm text-red-700">{error}</p>}
      {!check && !error && (
        <p className="m-0 mt-2 text-sm text-page-muted flex items-center gap-1.5">
          <Loader2 size={14} className="animate-spin" /> Checking…
        </p>
      )}
      {check?.status === 'no_repo' && (
        <p
          className="m-0 mt-2 text-sm text-page-muted flex items-center gap-1.5"
          data-testid="repo-check-not-connected"
        >
          <GitBranch size={14} /> Not connected: {check.message}.
        </p>
      )}
      {check && check.status !== 'no_repo' && (
        <>
          <p className="m-0 mt-2 text-xs text-page-muted flex items-center gap-1.5 flex-wrap">
            <GitBranch size={12} />
            <code>{check.commit?.slice(0, 12)}</code>
            {!check.current && (
              <span className="text-amber-700">(the head moved: checking the new commit)</span>
            )}
            · {check.format.format ?? 'no format marker'} · {check.stats.designFiles} design files ·{' '}
            {check.stats.ms} ms · {new Date(check.checkedAt).toLocaleTimeString()}
          </p>
          {check.link && (
            <p
              className="m-0 mt-2 text-xs text-page-text flex items-center gap-1.5"
              data-testid="repo-check-read-only"
            >
              <Lock size={12} /> Linked read-only by {check.link.setBy}: cortex never writes to this repo.
              <button
                type="button"
                className="bg-transparent border-0 p-0 text-nav-active cursor-pointer text-xs"
                onClick={() => void unlink()}
              >
                Unlink
              </button>
            </p>
          )}
          {check.status === 'not_cortex' ? (
            <NotCortex slug={slug} branch={branch} check={check} onLinked={() => void reload()} />
          ) : (
            <>
              <ul className="m-0 mt-3 p-0 list-none" data-testid="repo-check-modules">
                {check.modules.map((m) => (
                  <ModuleRow key={m.id} m={m} slug={slug} branch={branch} />
                ))}
              </ul>
              {check.quarantined && check.quarantined.length > 0 && (
                <div className="mt-3" data-testid="repo-check-quarantined">
                  <h3 className="text-sm font-medium m-0 mb-1 flex items-center gap-1.5 text-page-text">
                    <XCircle size={14} /> Skipped by cortex ({check.quarantined.length})
                  </h3>
                  <p className="m-0 mb-2 text-xs text-page-muted">
                    Quarantined: these files are not read until a commit fixes them; the rest of the branch
                    renders.
                  </p>
                  <ProblemList
                    items={check.quarantined.map((q) => ({
                      check: 'index',
                      file: q.path,
                      line: null,
                      message: q.reason,
                    }))}
                    tone="broken"
                  />
                </div>
              )}
              <ConfigureList items={check.configure} />
              <div
                className="mt-3 flex items-center gap-2 text-xs text-page-muted flex-wrap"
                data-testid="repo-check-schemas"
              >
                <FileJson size={12} />
                {check.schemas.missing.length || check.schemas.stale.length
                  ? `JSON Schemas in .cortex/schema/: ${check.schemas.missing.length} missing, ${check.schemas.stale.length} stale`
                  : 'JSON Schemas in .cortex/schema/ are current (IDEs and CI can validate every file)'}
                {(check.schemas.missing.length > 0 || check.schemas.stale.length > 0) && !check.link && (
                  <button type="button" className={BTN} disabled={!!busy} onClick={() => void publish()}>
                    {busy === 'schemas' ? <Loader2 size={14} className="animate-spin" /> : null} Publish
                    schemas
                  </button>
                )}
              </div>
              {check.aiTools ? (
                <div
                  className="mt-2 flex items-center gap-2 text-xs text-page-muted flex-wrap"
                  data-testid="repo-check-ai-tools"
                >
                  <Bot size={12} />
                  {check.aiTools.missing.length || check.aiTools.stale.length
                    ? `AI tool files: ${[...check.aiTools.missing.map((f) => `${f} missing`), ...check.aiTools.stale.map((f) => `${f} out of date`)].join(', ')}`
                    : 'AI tool files are current (AGENTS.md, CLAUDE.md): an AI tool that opens this repo knows how to work in it'}
                  {(check.aiTools.missing.length > 0 || check.aiTools.stale.length > 0) && !check.link && (
                    <button
                      type="button"
                      className={BTN}
                      disabled={!!busy}
                      onClick={() => void writeAiTools()}
                      data-testid="repo-check-ai-tools-write"
                    >
                      {busy === 'ai-tools' ? <Loader2 size={14} className="animate-spin" /> : null} Write them
                    </button>
                  )}
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Which module a project page renders (the page path after /p/<slug>/b/<branch>/). */
export function moduleOfPage(page: string): string | null {
  const [first, second] = page.split('/');
  if (first === 'apps' && second) return `app:${decodeURIComponent(second)}`;
  const map: Record<string, string> = {
    workflows: 'workflows',
    agents: 'agents',
    'data-elements': 'governance',
    'data-dictionary': 'governance',
    'processing-rules': 'governance',
    'reference-lists': 'governance',
    'source-mappings': 'governance',
    'access-policies': 'governance',
    pipeline: 'pipeline',
    'data-sources': 'pipeline',
    docs: 'requirements',
    guides: 'guides',
    models: 'models',
    status: 'tests',
    structure: 'structure',
  };
  return map[first ?? ''] ?? null;
}

/** One line above a project page whose module does not render cleanly; nothing when it does. */
export function RepoCheckStrip({ slug, branch, page }: { slug: string; branch: string; page: string }) {
  const id = moduleOfPage(page);
  const { check } = useRepoCheck(id ? slug : null, id ? branch : null);
  // Nothing to check (no repository yet): the page renders from what it has, no strip.
  if (!id || !check || check.status === 'no_repo') return null;
  const settings = projectPath('settings', slug, branch);
  if (check.status === 'not_cortex')
    return (
      <div
        className={`px-4 py-1.5 text-xs border-b flex items-center gap-2 ${TONE.not_cortex}`}
        data-testid="repo-check-strip"
      >
        <AlertTriangle size={12} /> The repository is not in the cortex format: this page has nothing to show
        from it.
        <Link to={settings} className="text-nav-active">
          Adopt or link read-only
        </Link>
      </div>
    );
  const m = check.modules.find((x) => x.id === id);
  if (!m || m.status === 'renderable') return null;
  const first = m.broken[0] ?? m.gaps[0];
  return (
    <div
      className={`px-4 py-1.5 text-xs border-b flex items-center gap-2 min-w-0 ${TONE[m.status]}`}
      data-testid="repo-check-strip"
    >
      <Icon s={m.status} size={12} />
      <span className="font-medium shrink-0">
        {m.label}: {LABEL[m.status].toLowerCase()}
      </span>
      {first && (
        <span className="truncate min-w-0">
          <code>{loc(first)}</code> {first.message}
        </span>
      )}
      {m.broken.length + m.gaps.length > 1 && (
        <span className="shrink-0">+{m.broken.length + m.gaps.length - 1} more</span>
      )}
      <Link to={settings} className="ml-auto shrink-0 text-nav-active">
        Repository status
      </Link>
    </div>
  );
}
